import type { paths } from '@cou/contracts';
import { adminApi, type ApiFailure } from '../api/admin-api';

/*
 * The Entries API client (API-22, admin-ui.md → Contest detail › Entries). Read-only: every Entry
 * mutation lives in the settlement tooling (API-11), so there is deliberately no write here.
 */

export type AdminEntryRow =
  paths['/api/admin/contests/{id}/entries']['get']['responses'][200]['content']['application/json']['entries'][number];
export type AdminEntryDetail =
  paths['/api/admin/entries/{id}']['get']['responses'][200]['content']['application/json'];

type ListEntriesBody =
  paths['/api/admin/contests/{id}/entries']['get']['responses'][200]['content']['application/json'];

export type ListEntriesResult = ({ ok: true } & ListEntriesBody) | ApiFailure;
export type GetEntryResult = { ok: true; entry: AdminEntryDetail } | ApiFailure;

/** The Entry states the tab filters by (CONTEXT → Entry). */
export const ENTRY_STATES = ['Editable', 'Locked', 'Settled', 'Voided'] as const;

export type ListEntriesFilter = {
  readonly status?: string;
  readonly limit?: number;
  readonly cursor?: string;
};

/** A Contest's Entries, filterable by state (API-22 GET). */
export async function listEntriesForContest(
  adminToken: string,
  contestId: string,
  filter: ListEntriesFilter,
): Promise<ListEntriesResult> {
  return adminApi.get<ListEntriesBody>(
    adminToken,
    `/api/admin/contests/${contestId}/entries`,
    filter,
  );
}

/** One Entry with its Picks and per-pick results (API-22 GET). */
export async function getEntry(adminToken: string, id: string): Promise<GetEntryResult> {
  const result = await adminApi.get<AdminEntryDetail>(adminToken, `/api/admin/entries/${id}`);
  if (!result.ok) return result;
  const { ok: _ok, ...entry } = result;
  return { ok: true, entry };
}

export interface EntriesClient {
  listEntriesForContest(
    adminToken: string,
    contestId: string,
    filter: ListEntriesFilter,
  ): Promise<ListEntriesResult>;
  getEntry(adminToken: string, id: string): Promise<GetEntryResult>;
}

export const defaultEntriesClient: EntriesClient = { listEntriesForContest, getEntry };
